"use client"

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Progress } from "@/components/ui/progress"
import { Target, TrendingUp, Award, Info } from "lucide-react"


interface ScoreBreakdownModalProps {
    isOpen: boolean
    onClose: () => void
    breakdown: {
        baseScore: number
        accuracyScore: number
        volumeBonus: number
        totalScore: number
        mathScore: number
        englishScore: number
    } | undefined
}

export function ScoreBreakdownModal({ isOpen, onClose, breakdown }: ScoreBreakdownModalProps) {
    if (!breakdown) return null

    const maxScore = 1600
    const progress = (breakdown.totalScore / maxScore) * 100

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight">
                        <Target className="w-5 h-5 text-purple-600" />
                        Projected Score Analysis
                    </DialogTitle>
                    <DialogDescription className="text-sm text-slate-500 dark:text-slate-400">
                        Based on your current performance and practice habits.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-8 py-2 md:py-4">
                    <div className="space-y-4">
                        {/* Main Equation Display */}
                        <div className="flex items-center justify-center text-slate-900 dark:text-slate-50 gap-2 sm:gap-6 bg-slate-50 dark:bg-zinc-800/30 p-6 rounded-2xl border border-dashed border-slate-200 dark:border-zinc-800">
                            <div className="text-center">
                                <div className="text-2xl sm:text-3xl font-bold tracking-tight">{breakdown.mathScore}</div>
                                <div className="text-sm font-semibold text-slate-500 underline decoration-purple-500/30 underline-offset-4 uppercase tracking-wider mt-1">Math</div>
                            </div>
                            <div className="text-2xl text-slate-300 font-light">+</div>
                            <div className="text-center">
                                <div className="text-2xl sm:text-3xl font-bold tracking-tight">{breakdown.englishScore}</div>
                                <div className="text-sm font-semibold text-slate-500 underline decoration-indigo-500/30 underline-offset-4 uppercase tracking-wider mt-1">English</div>
                            </div>
                            <div className="text-2xl text-slate-300 font-light">=</div>
                            <div className="text-center relative">
                                <div className="text-4xl sm:text-5xl font-bold text-primary tracking-tight leading-none">{breakdown.totalScore}</div>
                                <div className="text-sm font-bold text-primary/80 uppercase tracking-wider mt-1">Total Score</div>
                            </div>
                        </div>

                        <div className="relative pt-2">
                            <Progress value={progress} className="h-2.5 w-full" indicatorClassName="bg-gradient-to-r from-purple-500 to-indigo-600" />
                            <div className="flex justify-between text-sm font-medium text-muted-foreground mt-2 px-1">
                                <span>400</span>
                                <span>Target: 1600</span>
                            </div>
                        </div>
                    </div>

                </div>
            </DialogContent>
        </Dialog>
    )
}
